/**
 * Users — every registered account: phone, email, studios + roles,
 * sign-up date and platform, last activity, device and app version.
 * Search, filter, CSV export.
 *
 * Data: `adminListUsers`.
 */
import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconBrandWhatsapp, IconDownload, IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  PlatformBadge,
  RefreshButton,
  RelTime,
  useAdminData,
} from '@/components/ui';
import type { UserSummary } from '@/lib/adminTypes';
import { callAdmin } from '@/lib/firebase';
import {
  PLATFORM_LABEL,
  ROLE_LABEL,
  downloadCsv,
  fmtDate,
  fmtPhone,
  fmtRelative,
  label,
  waLink,
} from '@/lib/format';

const ACTIVITY = [
  { value: '7', label: 'Active in 7 days' },
  { value: '30', label: 'Active in 30 days' },
  { value: 'inactive30', label: 'Inactive 30+ days' },
];

const MEMBERSHIP = [
  { value: 'owner', label: 'Studio owners' },
  { value: 'member', label: 'Team members' },
  { value: 'client', label: 'Clients' },
  { value: 'none', label: 'No studio yet' },
];

function kind(u: UserSummary): 'owner' | 'member' | 'client' | 'none' {
  if (u.studios.length === 0) return 'none';
  if (u.studios.some((s) => s.role === 'superAdmin')) return 'owner';
  if (u.studios.some((s) => s.role !== 'client')) return 'member';
  return 'client';
}

export function Users() {
  const { data, error, loading, reload } = useAdminData(() => callAdmin.listUsers({}));
  const [search, setSearch] = useState('');
  const [signup, setSignup] = useState<string | null>(null);
  const [activity, setActivity] = useState<string | null>(null);
  const [membership, setMembership] = useState<string | null>(null);

  const rows = useMemo(() => {
    const now = Date.now();
    const q = search.trim().toLowerCase();
    return (data?.rows ?? []).filter((u) => {
      if (signup && u.signupPlatform !== signup) return false;
      if (membership && kind(u) !== membership) return false;
      if (activity) {
        const ago = now - (u.lastActiveAt ?? 0);
        if (activity === '7' && ago > 7 * 86_400_000) return false;
        if (activity === '30' && ago > 30 * 86_400_000) return false;
        if (activity === 'inactive30' && ago <= 30 * 86_400_000) return false;
      }
      if (!q) return true;
      return [u.phone, u.email, u.name, u.uid, ...u.studios.map((s) => s.name)]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [data, search, signup, activity, membership]);

  const exportCsv = () =>
    downloadCsv(
      `interiorio-users-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Phone', 'Name', 'Email', 'Studios', 'Signed up', 'Sign-up platform', 'Last active', 'Last platform', 'App version', 'Device', 'User ID'],
      rows.map((u) => [
        u.phone, u.name, u.email,
        u.studios.map((s) => `${s.name} (${label(ROLE_LABEL, s.role)})`).join('; '),
        fmtDate(u.createdAt), PLATFORM_LABEL[u.signupPlatform],
        u.lastActiveAt ? fmtDate(u.lastActiveAt) : null, PLATFORM_LABEL[u.lastPlatform],
        u.appVersion, u.deviceModel, u.uid,
      ]),
    );

  if (error && !data) return <ErrorBlock message={error} onRetry={reload} />;

  return (
    <>
      <PageHeader
        title="Users"
        subtitle={data ? `${rows.length} of ${data.rows.length} registered users` : 'Loading…'}
        actions={
          <>
            <Button variant="default" size="xs" leftSection={<IconDownload size={14} />} onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Button>
            <RefreshButton onClick={reload} loading={loading} />
          </>
        }
      />

      <Card withBorder radius="lg" p="md" mb="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            placeholder="Search phone, email, name or studio…"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: '1 1 260px' }}
          />
          <Select placeholder="Anyone" data={MEMBERSHIP} value={membership} onChange={setMembership} clearable w={170} />
          <Select
            placeholder="Any sign-up platform"
            data={(['ios', 'android', 'web', 'mobile', 'unknown'] as const).map((p) => ({ value: p, label: PLATFORM_LABEL[p] }))}
            value={signup}
            onChange={setSignup}
            clearable
            w={190}
          />
          <Select placeholder="Any activity" data={ACTIVITY} value={activity} onChange={setActivity} clearable w={180} />
        </Group>
      </Card>

      {!data ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <Card withBorder radius="lg">
          <EmptyState title="No users match these filters" />
        </Card>
      ) : (
        <Card withBorder radius="lg" p={0}>
          <Table.ScrollContainer minWidth={1000}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="#F8FAFC">
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Studios</Table.Th>
                  <Table.Th>Signed up</Table.Th>
                  <Table.Th>Last active</Table.Th>
                  <Table.Th>Device</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((u) => (
                  <Table.Tr key={u.uid}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" fw={600}>
                            {fmtPhone(u.phone)}
                          </Text>
                          {u.phone ? (
                            <Anchor href={waLink(u.phone) ?? undefined} target="_blank" rel="noreferrer" c="green" style={{ display: 'flex' }}>
                              <IconBrandWhatsapp size={14} />
                            </Anchor>
                          ) : null}
                          {!u.hasProfile ? (
                            <Badge size="xs" variant="outline" color="gray">
                              Signed in only
                            </Badge>
                          ) : null}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {[u.name, u.email].filter(Boolean).join(' · ') || '—'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {u.studios.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          No studio yet
                        </Text>
                      ) : (
                        <Stack gap={2}>
                          {u.studios.slice(0, 3).map((s) => (
                            <Group key={s.id} gap={6} wrap="nowrap">
                              <Anchor component={Link} to={`/studios/${s.id}`} size="sm" truncate maw={220}>
                                {s.name}
                              </Anchor>
                              <Badge size="xs" variant="light" color={s.role === 'superAdmin' ? 'brand' : s.role === 'client' ? 'gray' : 'indigo'}>
                                {label(ROLE_LABEL, s.role)}
                              </Badge>
                            </Group>
                          ))}
                          {u.studios.length > 3 ? (
                            <Text size="xs" c="dimmed">
                              +{u.studios.length - 3} more
                            </Text>
                          ) : null}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <Text size="sm">{fmtDate(u.createdAt)}</Text>
                        <PlatformBadge platform={u.signupPlatform} />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <RelTime ms={u.lastActiveAt} />
                        {u.lastPlatform !== 'unknown' ? <PlatformBadge platform={u.lastPlatform} /> : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{u.deviceModel ?? '—'}</Text>
                        <Text size="xs" c="dimmed">
                          {[u.osVersion ? `OS ${u.osVersion}` : null, u.appVersion ? `app v${u.appVersion}` : null, u.pushEnabled ? 'push on' : null]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      {data ? (
        <Text size="xs" c="dimmed" mt="sm">
          Updated {fmtRelative(data.generatedAt)}
        </Text>
      ) : null}
    </>
  );
}
